/**
 * Where a new tile goes when nobody picked a spot: the first gap that fits, scanning the
 * grid row by row and left to right, else the tail of the canvas. Pure, on canvas grid
 * units (the caller converts the answer to stored units).
 */

export interface FreeSpotRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FreeSpot {
  x: number;
  y: number;
}

const overlaps = (a: FreeSpotRect, b: FreeSpotRect): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/**
 * The first free spot for a `w` x `h` tile on a `cols`-column grid.
 *
 * `layout` holds the tiles already placed. Candidate columns step by `step` (default 1), so
 * a canvas that stores positions on a coarser grid gets a spot it can save exactly. A tile
 * wider than the grid is clamped to it. When nothing fits above the last row, the answer is
 * `{ x: 0, y: <bottom of the lowest tile> }`.
 */
export const findFreeSpot = (
  layout: readonly FreeSpotRect[],
  w: number,
  h: number,
  cols: number,
  step = 1,
): FreeSpot => {
  const width = Math.max(1, Math.min(w, cols));
  const height = Math.max(1, h);
  const stride = Math.max(1, Math.floor(step));
  const tiles = layout.filter((tile) => tile.w > 0 && tile.h > 0);
  const bottom = tiles.reduce((lowest, tile) => Math.max(lowest, tile.y + tile.h), 0);

  for (let y = 0; y < bottom; y += 1) {
    for (let x = 0; x + width <= cols; x += stride) {
      const candidate = { x, y, w: width, h: height };
      if (!tiles.some((tile) => overlaps(candidate, tile))) return { x, y };
    }
  }
  return { x: 0, y: bottom };
};
